import React, { useState } from 'react';


import { makeStyles } from 'tss-react/mui';
import { Box, Button, Pill, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { AnnouncementDetail } from 'web-library/models/Announcements';
import parse from 'html-react-parser';
import { stringToSemanticDisplay } from '../../utils/DateUtils';
import AnnouncementContent from '../../modules/announcement/components/AnnouncementContent';

const useStyles = makeStyles()((theme) =>
({
  announcementCardContainer: {
    marginTop: theme.spacing(3.5),
  },
  cardHeaderContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 34,
  },
  dateContainer: {
    color: theme.color.text.textTertiary,
    marginBottom: theme.spacing(1.5),
  },
  titleContainer: {
    marginBottom: theme.spacing(1),
  },
  descriptionContainer: {
    fontWeight: 400,
    marginBottom: theme.spacing(3),
  },
  readMoreButton: {
    marginBottom: theme.spacing(3),
    padding: 0,
    minWidth: 0,
    color: theme.color.link.linkDefault01,
    '&:hover': {
      color: theme.color.link.linkHover01,
    },
  },
  readMoreIcon: {
    flexDirection: 'row-reverse',
  },
  readMoreLabel: {
    fontWeight: 800,
    fontSize: '16px',
  },
}));

interface AnnouncementCardDetailsProps {
  announcementNoticeDetails: AnnouncementDetail;
}

const AnnouncementCardDetail = ({ announcementNoticeDetails }: AnnouncementCardDetailsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [openAnnouncementModal, setOpenAnnouncementModal] = useState<boolean>(false);

  const [announcementUuid, setAnnouncementUuid] = useState<string>('');

  const handleOpenReadMoreModal = () => {
    setAnnouncementUuid(announcementNoticeDetails.uuid || '');
    setOpenAnnouncementModal(true);
  };

  return (
    <Box className={classes.announcementCardContainer}>
      {announcementNoticeDetails?.announcementStartDate && announcementNoticeDetails?.priority && (
        <Box className={classes.cardHeaderContent}>
          {announcementNoticeDetails?.announcementStartDate && (
            <Typography variant="subtitle2" className={classes.dateContainer}>
              {stringToSemanticDisplay(announcementNoticeDetails?.announcementStartDate || '')}
            </Typography>
          )}
          {announcementNoticeDetails?.priority && (
            <Pill label={t('admin->>AnnouncementNoticeBoard.Priority')} variant="warning" />
          )}
        </Box>
      )}
      {announcementNoticeDetails?.title && (
        <Typography variant="h4" className={classes.titleContainer}>
          {announcementNoticeDetails?.title}
        </Typography>
      )}
      {announcementNoticeDetails?.description && (
        <Typography variant="body1" className={classes.descriptionContainer}>
          {parse(announcementNoticeDetails?.description || '')}
        </Typography>
      )}
      <Button
        variant="text"
        size="small"
        className={classes.readMoreButton}
        onClick={handleOpenReadMoreModal}
      >
        <IconWithLabel
          iconName={IconName.arrowRight}
          className={classes.readMoreIcon}
          typographyClassName={classes.readMoreLabel}
          gap={4}
          typographyProps={{
            variant: 'subtitle2',
          }}
          label={t('admin->>AnnouncementNoticeBoard.ReadMore')}
        />
      </Button>
      <AnnouncementContent
        openAnnouncementModal={openAnnouncementModal}
        setOpenAnnouncementModal={setOpenAnnouncementModal}
        isPriorityNotice={false}
        announcementType="ANNOUNCEMENTS"
        announcementUuid={announcementUuid}
      />
    </Box>
  );
};

export default AnnouncementCardDetail;
